import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { Loader2, AlertCircle, CheckCircle2, ArrowRight } from "lucide-react";
import { OrderSlip } from "@/components/order-slip";
import {
  getAttribution,
  getSchedulerUrl,
  appendParams,
  track,
  trackLead,
} from "@/lib/analytics";

/**
 * Post-order demo recap + native lead capture (Phase 0 task 6).
 *
 * Replaces the old Tally redirect: the prospect who just built a demo order can
 * request a walkthrough right here. Submitting captures their email server-side
 * (a CASL consent record + an instant founder notification) and — if a scheduler
 * URL is configured — hands them straight to it with their email pre-filled.
 */
export default function DemoAdminView() {
  const params = useParams<{ orderId: string }>();
  const orderId = parseInt(params.orderId ?? "", 10);

  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNaN(orderId)) {
      setError("Order not found.");
      setLoading(false);
      return;
    }

    fetch(`/demo/admin/order/${orderId}`)
      .then(async (res) => {
        if (!res.ok) {
          setError("Order not found.");
          return;
        }
        const data = await res.json();
        setOrder(data);
        track("demo_recap_viewed", { demo_order_id: data?.id });
      })
      .catch(() => setError("Failed to load order."))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-3">
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto" />
          <p className="text-gray-600">{error ?? "Order not found."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky demo banner. Extra top padding = the iOS status-bar inset (the native shell is
          full-bleed under viewport-fit=cover); resolves to 0 on the web. */}
      <div
        className="sticky top-0 z-10 bg-cn-brand-blue text-white text-center py-3 px-4 text-sm font-medium shadow"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
      >
        You submitted an order! Want to see how it flows into buy-lists, pick sheets, and QuickBooks invoices?{" "}
        <span className="underline">Book a walkthrough below</span>.
      </div>

      {/* Order detail */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-8">
        <OrderSlip
          order={order}
          isOpen={true}
          onClose={() => {}}
          readOnly={true}
          showItemThumbnails={true}
        />
      </main>

      {/* Native lead capture (replaces the old Tally handoff) */}
      <footer className="border-t bg-white py-10 px-4">
        <WalkthroughCta order={order} />
      </footer>
    </div>
  );
}

function WalkthroughCta({ order }: { order: any }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const schedulerUrl = getSchedulerUrl();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    const trimmed = email.trim();
    // Minimal client check; the server re-validates.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setFormError("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    const attribution = getAttribution();
    try {
      const res = await fetch("/demo/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: trimmed,
          name: name.trim() || undefined,
          company: company.trim() || undefined,
          demoOrderId: order?.id,
          ...attribution,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setFormError(data?.message ?? "Something went wrong. Please try again.");
        setSubmitting(false);
        return;
      }

      trackLead({ demo_order_id: order?.id, has_scheduler: !!schedulerUrl });
      setSubmitted(true);
      setSubmitting(false);

      // Hand straight off to the scheduler when configured.
      const target = schedulerLink(schedulerUrl);
      if (target) {
        window.open(target, "_blank", "noopener,noreferrer");
      }
    } catch {
      setFormError("Network error. Please try again.");
      setSubmitting(false);
    }
  }

  if (submitted) {
    const target = schedulerLink(schedulerUrl);
    return (
      <div className="max-w-md mx-auto text-center">
        <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">You're all set</h3>
        {target ? (
          <>
            <p className="text-gray-600 mb-5">
              A booking window {`should have opened in a new tab`}. If it didn't, grab a time here:
            </p>
            <a
              href={target}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("demo_scheduler_opened", { source: "success_fallback" })}
              className="inline-flex items-center gap-2 bg-cn-brand-blue text-white px-8 py-3 rounded-lg font-semibold hover:bg-cn-brand-blue/90 transition-colors"
            >
              Pick a time <ArrowRight className="h-4 w-4" />
            </a>
          </>
        ) : (
          <p className="text-gray-600">
            Thanks — we'll be in touch shortly to set up your walkthrough with <strong>your</strong> products loaded.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-5">
        <p className="text-lg font-bold text-gray-900">Ready to tour the back-end?</p>
        <p className="text-gray-600 text-sm mt-1">
          Leave your email and we'll walk you through pick sheets, buy-lists, and one-click QuickBooks invoices —
          with your products loaded, not a canned demo.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourcompany.com"
          autoComplete="email"
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-cn-brand-blue"
        />
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (optional)"
            autoComplete="name"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-cn-brand-blue"
          />
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Company (optional)"
            autoComplete="organization"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-cn-brand-blue"
          />
        </div>
        {formError && <p className="text-sm text-red-600">{formError}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-cn-brand-blue text-white px-8 py-3 rounded-lg font-semibold hover:bg-cn-brand-blue/90 transition-colors disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {schedulerUrl ? "Book my walkthrough" : "Request a walkthrough"} <ArrowRight className="h-4 w-4" />
        </button>
        <p className="text-xs text-gray-400 text-center">No spam. We'll only use this to arrange your walkthrough.</p>
      </form>
    </div>
  );
}

/**
 * Build the scheduler URL with UTM tags.
 * Google Calendar appointment pages ignore `email`/`name` prefill params (and drop
 * any query string when the calendar.app.google short link 302-redirects), so we no
 * longer append them. If we ever move back to Calendly/cal.com, re-add email/name here.
 */
function schedulerLink(base: string | null): string | null {
  if (!base) return null;
  return appendParams(base, {
    utm_source: "app_demo",
    utm_medium: "demo_walkthrough",
    utm_campaign: "demo_funnel",
  });
}
